<script>
  import { v4 as uuidv4 } from "uuid";
  import { gsap } from "gsap";
  import { createEventDispatcher } from "svelte";
  import Target from "./Target.svelte";

  export let icon = "The icon";
  export let title = "The title";
  export let text = "The text";
  export let textOffset = "50%";
  export let inverted = false;
  export let dark = false;
  export let alignRight = false;

  const dispatch = createEventDispatcher();

  const componentId = "characteristics-block__" + uuidv4();

  let displayed = false;

  export function show() {
    const tl = gsap.timeline();
    tl.to("#" + componentId + " .characteristics-block__separator-box", {
      opacity: 1,
      duration: 0.1,
    });
    tl.to(
      "#" + componentId + " .characteristics-block__separator-target .target",
      {
        boxShadow: !dark
          ? "0 0 8px 8px #869ac390 inset, 0 0 8px 8px #869ac390"
          : "0 0 8px 8px #af6d2290 inset, 0 0 8px 8px #af6d2290",
        scale: 1.1,
        duration: 0.01,
      },
      "<"
    );
    tl.to(
      "#" + componentId + " .characteristics-block__text",
      {
        opacity: 1,
        duration: 0.2,
      },
      ">-0.05"
    );
    tl.play();
  }

  export function hide() {
    const tl = gsap.timeline();
    tl.to("#" + componentId + " .characteristics-block__text", {
      opacity: 0,
      duration: 0.1,
    });
    tl.to(
      "#" + componentId + " .characteristics-block__separator-target .target",
      {
        clearProps: "all",
        duration: 0.01,
      },
      "<"
    );
    tl.to(
      "#" + componentId + " .characteristics-block__separator-box",
      {
        opacity: 0,
        duration: 0.1,
      },
      ">-0.05"
    );
    tl.play();
  }

  function handleClick(event) {
    dispatch("click", event);
  }
</script>

<div
  id={componentId}
  class="characteristics-block"
  class:characteristics-block--inverted={inverted}
  class:characteristics-block--dark={dark}
  class:characteristics-block--align-right={alignRight}
>
  <div class="characteristics-block__header-container">
    <div class="characteristics-block__placeholder" />
    <div class="characteristics-block__separator">
      <div
        class="characteristics-block__separator-target"
        style="left: calc({textOffset} - 23px)"
      >
        <Target {dark} on:click={handleClick} />
      </div>
      <div
        class="characteristics-block__separator-box"
        style="left: {textOffset}"
      />
    </div>
  </div>
  <div class="characteristics-block__text">
    <div class="characteristics-block__title">
      {title.toLocaleUpperCase()}
    </div>
    <div class="characteristics-block__description">
      {text}
    </div>
  </div>
</div>

<style lang="scss">
  @import "./assets/styles/variables";
  @import "./assets/styles/animations";

  $text-color-light: white;
  $text-color-dark: white;
  $background-color-light: rgba(#5d6f82, 0.1);
  $background-color-dark: rgba(0, 0, 0, 0.4);
  $separator-color-light: white;
  $separator-color-dark: white;

  .characteristics-block {
    display: flex;
    color: $text-color-light;
    height: 100%;
    flex-direction: column;
    font-family: "Roboto";

    &__header-container {
      display: flex;
      flex-direction: column;
      flex-grow: 1;
      justify-content: stretch;
      position: relative;
    }

    &--inverted &__header-container {
      order: 2;
    }

    &__placeholder {
      flex-grow: 1;
    }

    &__text {
      background-color: $background-color-light;
      border-top: 2px solid $separator-color-light;
      padding: 0.75rem;
      opacity: 0;

      @media screen and (min-width: $breakpoint-medium) {
        padding: 1rem;
      }
    }

    &--inverted &__text {
      border-bottom: 2px solid $separator-color-light;
      border-top: none;
    }

    &--dark &__text {
      background-color: $background-color-dark;
    }

    &__title {
      font-size: 16px;
      line-height: 20px;
      font-style: italic;
      font-weight: bold;
      text-transform: uppercase;

      @media screen and (min-width: $breakpoint-medium) {
        font-size: 16px;
        line-height: 20px;
      }

      @media screen and (min-width: $breakpoint-large) {
        font-size: 24px;
      }
    }

    &__description {
      font-size: 12px;
      line-height: 18px;
      margin-top: 12px;

      @media screen and (min-width: $breakpoint-medium) {
        font-size: 12px;
        line-height: 18px;
      }

      @media screen and (min-width: $breakpoint-large) {
        font-size: 16px;
        line-height: 20px;
      }
    }

    &__separator {
      margin: 0.75rem 0 0 0;

      &-box {
        border-left: 2px dotted $separator-color-light;
        bottom: 0;
        max-width: 100%;
        opacity: 0;
        position: absolute;
        width: 13rem;
        top: 48px;
        /*visibility: hidden;*/
      }

      &-target {
        position: absolute;
        top: 0;
        z-index: 1;

        &:hover {
          cursor: pointer;
        }
      }
    }

    &--inverted &__separator {
      &-box {
        bottom: unset;
        top: 0;
        height: calc(100% - 48px);
      }

      &-target {
        bottom: 0;
        top: unset;
      }
    }

    &--dark {
      color: $text-color-dark;
    }

    &--align-right {
      align-items: flex-end;
      text-align: right;
    }

    &--dark &__separator {
      &-box {
        border-color: $separator-color-dark;
        filter: drop-shadow(0px 0px 8px $separator-color-dark)
          drop-shadow(0px 0px 16px $separator-color-dark);
      }
    }
  }
</style>
